// 감사로그 페이지 — 필터 + 페이지네이션 + CSV 익스포트. 컴플라이언스 시연용.
import { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { api } from '../lib/api'
import { useT } from '../lib/i18n'
import { EmptyState } from '../components/EmptyState'
import { SkeletonRows } from '../components/Skeleton'
import { Button } from '../components/Button'

type AuditRow = {
  audit_id: string
  user_id: string | null
  action: string
  resource: string | null
  resource_id: string | null
  ip_address: string | null
  user_agent: string | null
  detail: string | null
  created_at: string
}

type Facet = { v: string; n: number }

const PAGE_SIZE = 50

export function AuditPage() {
  const { t } = useT()
  const [items, setItems] = useState<AuditRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionFilter, setActionFilter] = useState('')
  const [resourceFilter, setResourceFilter] = useState('')
  const [userIdFilter, setUserIdFilter] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(0)
  const [actions, setActions] = useState<Facet[]>([])
  const [resources, setResources] = useState<Facet[]>([])

  const params = useMemo(() => {
    const p = new URLSearchParams()
    if (actionFilter)   p.set('action', actionFilter)
    if (resourceFilter) p.set('resource', resourceFilter)
    if (userIdFilter)   p.set('user_id', userIdFilter)
    if (from) p.set('from', new Date(from).toISOString())
    if (to)   p.set('to',   new Date(to).toISOString())
    p.set('limit',  String(PAGE_SIZE))
    p.set('offset', String(page * PAGE_SIZE))
    return p.toString()
  }, [actionFilter, resourceFilter, userIdFilter, from, to, page])

  useEffect(() => {
    setLoading(true)
    setError(null)
    api
      .get<{ items: AuditRow[]; total: number }>(`/audit?${params}`)
      .then((r) => { setItems(r.items); setTotal(r.total) })
      .catch((e) => setError(e?.message ?? '불러올 수 없습니다.'))
      .finally(() => setLoading(false))
  }, [params])

  useEffect(() => {
    api.get<{ actions: Facet[]; resources: Facet[] }>('/audit/facets')
      .then((r) => { setActions(r.actions); setResources(r.resources) })
      .catch(() => {})
  }, [])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const downloadCsv = () => {
    const url = `${(import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '')}/api/audit/export.csv?${params}`
    window.open(url, '_blank', 'noopener')
  }

  return (
    <main id="main" className="container" style={{ padding: '2rem 1rem' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ margin: 0 }}><FontAwesomeIcon icon="shield-halved" /> {t('nav.audit')}</h1>
          <p className="muted" style={{ margin: '0.35rem 0 0' }}>모든 변경·인증 이벤트가 영구 기록됩니다 (PII 마스킹 적용).</p>
        </div>
        <Button variant="ghost" onClick={downloadCsv} style={{ marginLeft: 'auto' }}>
          <FontAwesomeIcon icon="arrow-up-right-from-square" /> CSV 내보내기
        </Button>
      </header>

      <section
        className="glass"
        style={{
          padding: '1rem',
          marginBottom: '1.5rem',
          display: 'grid',
          gap: '0.75rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        }}
      >
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>액션</span>
          <select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(0) }}>
            <option value="">전체</option>
            {actions.map((a) => <option key={a.v} value={a.v}>{a.v} ({a.n})</option>)}
          </select>
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>리소스</span>
          <select value={resourceFilter} onChange={(e) => { setResourceFilter(e.target.value); setPage(0) }}>
            <option value="">전체</option>
            {resources.map((r) => <option key={r.v} value={r.v}>{r.v} ({r.n})</option>)}
          </select>
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>사용자 ID</span>
          <input type="text" value={userIdFilter} onChange={(e) => { setUserIdFilter(e.target.value); setPage(0) }} placeholder="usr_xxx" />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>시작</span>
          <input type="datetime-local" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0) }} />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>종료</span>
          <input type="datetime-local" value={to} onChange={(e) => { setTo(e.target.value); setPage(0) }} />
        </label>
      </section>

      <section className="glass" style={{ padding: '0.5rem', overflowX: 'auto' }}>
        {loading ? (
          <SkeletonRows rows={8} />
        ) : error ? (
          <EmptyState icon="triangle-exclamation" title="조회 실패" hint={error} />
        ) : items.length === 0 ? (
          <EmptyState icon="shield-halved" title="조건에 맞는 감사 기록이 없습니다" hint="필터를 조정해보세요." />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
            <thead>
              <tr style={{ background: 'var(--color-line)' }}>
                <th style={cellHead}>시각</th>
                <th style={cellHead}>액션</th>
                <th style={cellHead}>리소스</th>
                <th style={cellHead}>사용자</th>
                <th style={cellHead}>IP</th>
                <th style={cellHead}>상세</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.audit_id} style={{ borderBottom: '1px solid var(--color-line)' }}>
                  <td style={cell}>{r.created_at}</td>
                  <td style={cell}><code>{r.action}</code></td>
                  <td style={cell}>{r.resource ?? '-'}{r.resource_id ? ` / ${r.resource_id}` : ''}</td>
                  <td style={cell}>{r.user_id ?? '-'}</td>
                  <td style={cell}>{r.ip_address ?? '-'}</td>
                  <td style={{ ...cell, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.detail ?? ''}>
                    {r.detail ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {!loading && items.length > 0 && (
        <nav aria-label="페이지" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.25rem' }}>
          <Button variant="ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>이전</Button>
          <span className="muted">{page + 1} / {totalPages} · 총 {total.toLocaleString()}건</span>
          <Button variant="ghost" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>다음</Button>
        </nav>
      )}
    </main>
  )
}

const cellHead: React.CSSProperties = { textAlign: 'left', padding: '0.6rem 0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }
const cell: React.CSSProperties = { padding: '0.55rem 0.75rem', verticalAlign: 'top' }
